/**
 * Прямое чтение из блокчейна.
 *
 * Основное чтение идёт через индексер — он быстрее и не жжёт лимиты у
 * каждого пользователя. Сюда вынесено то, для чего индексер не подходит:
 *
 *   баланс кошелька человека — он не от сделки зависит, и индексер про
 *   него ничего не знает;
 *
 *   свежее состояние сделки перед отправкой денег — снимок в базе может
 *   отставать, а платить по устаревшим числам опасно.
 */

import { Address, Cell } from '@ton/core';

/**
 * Куда ходить за цепочкой.
 *
 * По умолчанию — через наш сервер: у него есть ключ TON API, а у браузера
 * человека нет, и без ключа лимит на запросы общий на весь его адрес.
 * Отказ по лимиту приходит как обычная ошибка сети, и экран, читающий
 * цепочку, становится пустым — так «Мои NFT» и показывали «выставлять
 * нечего» человеку с полусотней вещей.
 *
 * Переменная сборки остаётся: с ней можно ходить напрямую — в разработке
 * без поднятого сервера или в тестовой сети.
 */
const DIRECT = import.meta.env['VITE_TON_API_URL'];
const API = DIRECT === undefined || DIRECT === '' ? '/api/ton' : DIRECT;

interface StackItem {
  type?: string;
  num?: string;
  cell?: string;
  slice?: string;
}

/**
 * Адрес из ответа get-метода.
 *
 * TON API отдаёт адрес то готовой строкой в `decoded`, то сырой ячейкой в
 * стеке — что именно, зависит от того, знает ли он ABI контракта. Нашего он
 * не знает, и на этом раздел управления однажды уже пропал: сравнивалась
 * строка «b5ee9c72…» с адресом кошелька, и админ не узнавал сам себя.
 */
function addressFrom(body: { decoded?: Record<string, unknown>; stack?: StackItem[] }): string | null {
  const decoded = body.decoded === undefined ? undefined : Object.values(body.decoded)[0];
  if (typeof decoded === 'string' && decoded.length > 0 && !decoded.startsWith('b5ee')) {
    // Сырой вид, а не дружественный: дружественных у одного адреса два
    // (EQ и UQ), отличаются они контрольной суммой, и сравнивать такие
    // строки между собой нельзя.
    return Address.parse(decoded).toRawString();
  }
  const encoded = body.stack?.[0]?.slice ?? body.stack?.[0]?.cell;
  if (encoded === undefined || encoded === '') return null;
  try {
    const parsed = Cell.fromBoc(Buffer.from(encoded, 'hex'))[0]?.beginParse().loadMaybeAddress();
    return parsed === null || parsed === undefined ? null : parsed.toRawString();
  } catch {
    // Не ячейка с адресом — значит и адреса здесь нет.
    return null;
  }
}

async function runGetMethod(account: string, method: string, args: string[] = []): Promise<StackItem[]> {
  const query = args.length === 0 ? '' : `?${args.map((a) => `args=${encodeURIComponent(a)}`).join('&')}`;
  const res = await fetch(`${API}/v2/blockchain/accounts/${account}/methods/${method}${query}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('network');
  const body = (await res.json()) as { stack?: StackItem[]; exit_code?: number; decoded?: unknown };
  if (body.exit_code !== undefined && body.exit_code !== 0 && body.exit_code !== 1) {
    throw new Error(`network`);
  }
  return body.stack ?? [];
}


/** Сколько внесено по сделке прямо сейчас. Читается перед платежом. */
export async function getPaidTotal(dealAddress: string): Promise<bigint> {
  const stack = await runGetMethod(dealAddress, 'paidTotal');
  const num = stack[0]?.num;
  if (num === undefined) throw new Error('network');
  return BigInt(num);
}

/**
 * Зависшая выплата: сколько контракт должен этому адресу.
 *
 * Появляется, когда перевод не дошёл и вернулся баунсом. Индексер об этом не
 * знает — сумма лежит в словаре внутри сделки, и спрашивать надо контракт.
 */
export async function getOwedTo(dealAddress: string, who: string): Promise<bigint> {
  const stack = await runGetMethod(dealAddress, 'owedTo', [who]);
  const num = stack[0]?.num;
  return num === undefined ? 0n : BigInt(num);
}

/**
 * Кому не доехал гифт, если не доехал. §9.5
 *
 * Спрашиваем контракт: индексер об этом знает только по отскоку, а человеку
 * кнопка нужна именно на своей сделке.
 */
export async function getNftOwedTo(dealAddress: string): Promise<string | null> {
  const stack = await runGetMethod(dealAddress, 'nftOwedTo');
  const cell = stack[0]?.cell ?? stack[0]?.slice;
  if (cell === undefined) return null;
  const parsed = cellFromBoc(cell).beginParse().loadMaybeAddress();
  return parsed === null ? null : parsed.toRawString();
}

/**
 * Кто владеет айтемом прямо сейчас.
 *
 * Спрашивается перед залогом, а не после отказа. Выставленным на продажу
 * NFT владеет контракт площадки, и перевод из кошелька он отвергнет —
 * человек увидел бы отказ кошелька без объяснения и потерял газ. Список
 * своих NFT для этого не годится: пока человек заполнял условия, лот мог
 * уехать на площадку.
 */
export async function getNftOwner(nftAddress: string): Promise<string | null> {
  const stack = await runGetMethod(nftAddress, 'get_nft_data');
  // Порядок TEP-62: init?, index, collection_address, owner_address, content
  const cell = stack[3]?.cell ?? stack[3]?.slice;
  if (cell === undefined) return null;
  const parsed = cellFromBoc(cell).beginParse().loadMaybeAddress();
  return parsed === null ? null : parsed.toRawString();
}

/**
 * Точный индекс айтема — из самого контракта.
 *
 * Списки TON API отдают индекс числом, а JSON превращает его в double: у
 * коллекций, где индекс считается из хэша имени, теряются младшие разряды.
 * Индекс участвует в вычислении адреса айтема мастером, и ошибка в одной
 * цифре — это другой адрес и отказ контракта после оплаченного газа.
 *
 * Спрашиваем get_nft_data: там индекс лежит целым числом, и это ровно то
 * значение, с которым будет считать мастер.
 */
export async function getNftIndex(nftAddress: string): Promise<bigint> {
  const stack = await runGetMethod(nftAddress, 'get_nft_data');
  // Порядок TEP-62: init?, index, collection_address, owner_address, content
  const num = stack[1]?.num;
  if (num === undefined) throw new Error('network');
  return BigInt(num);
}

/**
 * Баланс кошелька в нанотонах.
 *
 * Профиль показывает его, потому что первый вопрос перед взносом — «хватит
 * ли». Валюта у сделки одна и родная, поэтому это просто баланс аккаунта.
 */
export async function getTonBalance(address: string): Promise<bigint | null> {
  const res = await fetch(`${API}/v2/accounts/${encodeURIComponent(address)}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;
  const body = (await res.json()) as { balance?: number | string };
  return body.balance === undefined ? null : BigInt(body.balance);
}

export interface OwnedNft {
  address: string;
  /** Из списка TON API: годится показать, но не считать по нему адрес. */
  index: string;
  collection: string | null;
  collectionName: string | null;
  name: string | null;
  image: string | null;
  /** Выставлен на продажу: владеет им контракт площадки, а не человек. */
  onSale: boolean;
  /** Площадка, где он выставлен, — если она себя называет. */
  market: string | null;
}

/**
 * NFT на кошельке продавца.
 *
 * Нужны, чтобы оффер создавался выбором из своего, а не вводом адреса
 * руками: адрес айтема человек не знает, а ошибка в нём отправила бы NFT
 * в чужой контракт.
 *
 * Выставленные на продажу спрашиваются вместе с остальными
 * (`indirect_ownership=true`) и помечаются, а не выбрасываются. Пока их
 * не спрашивали, человек с одним выставленным гифтом видел «на этом
 * кошельке нет NFT» — и это выглядело поломкой приложения, а не тем, чем
 * было: пока лот на площадке, владеет им контракт продажи, и заложить его
 * нельзя, не сняв с продажи.
 */
/**
 * Что лежит на кошельке. Страница одна: листать здесь нечего — экран
 * показывает всё сразу, а сотня вещей на кошельке не редкость, и обрезанный
 * до сотни список молча терял то, что человек как раз искал.
 */
export async function fetchOwnedNfts(owner: string, limit = 500): Promise<OwnedNft[]> {
  const res = await fetch(
    `${API}/v2/accounts/${encodeURIComponent(owner)}/nfts?limit=${limit}&indirect_ownership=true`,
    { headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error('network');
  const body = (await res.json()) as {
    nft_items?: {
      address: string;
      index: number | string;
      collection?: { address: string; name?: string };
      metadata?: { name?: string; image?: string };
      previews?: { resolution: string; url: string }[];
      sale?: { address: string; market?: { name?: string } };
    }[];
  };
  return (body.nft_items ?? []).map((item) => ({
    address: item.address,
    index: String(item.index),
    collection: item.collection?.address ?? null,
    collectionName: item.collection?.name ?? null,
    name: item.metadata?.name ?? null,
    image:
      item.previews?.find((p) => p.resolution === '100x100')?.url ?? item.metadata?.image ?? null,
    onSale: item.sale !== undefined,
    market: item.sale?.market?.name ?? null,
  }));
}

/**
 * Админ мастера — читается из контракта, а не из настроек.
 *
 * Панель показывается тому, кто ею вправе пользоваться, и право это
 * определяет контракт: он и отвергнет чужую команду. Проверка на клиенте
 * нужна не для защиты, а чтобы не показывать кнопки, которые всё равно не
 * сработают.
 */
export async function getMasterAdmin(masterAddress: string): Promise<string> {
  const res = await fetch(
    `${API}/v2/blockchain/accounts/${encodeURIComponent(masterAddress)}/methods/admin`,
    { headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error('network');
  const address = addressFrom(
    (await res.json()) as { decoded?: Record<string, unknown>; stack?: StackItem[] },
  );
  if (address === null) throw new Error('network');
  return address;
}

/**
 * Кому предложено принять администрирование. Пусто — предложения нет.
 *
 * Читается из контракта: панель показывает незавершённую передачу тому, кто
 * её начал, и тому, кому предложили, — иначе второй шаг некому сделать.
 */
export async function getMasterNextAdmin(masterAddress: string): Promise<string | null> {
  const res = await fetch(
    `${API}/v2/blockchain/accounts/${encodeURIComponent(masterAddress)}/methods/nextAdmin`,
    { headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error('network');
  return addressFrom((await res.json()) as { decoded?: Record<string, unknown>; stack?: StackItem[] });
}

/**
 * Ключ приглашений, который стоит в мастере.
 *
 * Он обязан совпадать с тем, которым подписывает сервер. Разойдутся — ничего
 * не сломается заметно: контракт просто перестанет принимать приглашения, а
 * приложение продолжит их выдавать. Поэтому ключ и читается прямо из
 * контракта, а не берётся с сервера — сверять надо именно источники.
 */
export async function getMasterRefPubkey(masterAddress: string): Promise<bigint> {
  const res = await fetch(
    `${API}/v2/blockchain/accounts/${encodeURIComponent(masterAddress)}/methods/refPubkey`,
    { headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error('network');
  const body = (await res.json()) as { decoded?: Record<string, string>; stack?: StackItem[] };
  const raw = (body.decoded === undefined ? undefined : Object.values(body.decoded)[0]) ??
    body.stack?.[0]?.num;
  if (raw === undefined) throw new Error('network');
  return BigInt(raw);
}

/**
 * Код контракта по адресу.
 *
 * Реестру нужен код айтема коллекции: мастер по нему считает адрес
 * экземпляра, потому что синхронно спросить коллекцию в TON нельзя. Взять
 * его неоткуда, кроме как у уже существующего айтема этой коллекции.
 */
/**
 * Ячейка из того, что отдал TON API.
 *
 * Он отдаёт BoC в hex, а не в base64 — и `Cell.fromBase64` на нём падает с
 * «invalid magic». Ровно на этом спотыкалось внесение валюты долга в реестр:
 * ошибка выглядела как проблема с адресом, хотя адрес был верным.
 */
export function cellFromBoc(encoded: string): Cell {
  const raw = encoded.startsWith('0x') ? encoded.slice(2) : encoded;
  return /^[0-9a-fA-F]+$/.test(raw) && raw.length % 2 === 0
    ? Cell.fromBoc(Buffer.from(raw, 'hex'))[0]!
    : Cell.fromBase64(raw);
}

export async function getAccountCode(address: string): Promise<string> {
  const res = await fetch(`${API}/v2/blockchain/accounts/${encodeURIComponent(address)}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('network');
  const body = (await res.json()) as { code?: string; status?: string };
  if (body.code === undefined || body.code.length === 0) throw new Error('no_code');
  return body.code;
}
